import { useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  toast,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import { CategorySearchSelect } from '@/features/catalog/components/CategorySearchSelect';
import { BrandSelector } from '@/features/catalog/components/BrandSelector';
import { ProductMultiSelectModalField } from '@/features/catalog/components/ProductMultiSelectModalField';
import { useAdminCategoryItemsQuery, categoryDisplayName } from '@/features/catalog/hooks/useCategoriesQuery';
import { useBrandsQuery } from '@/features/catalog/hooks/useBrandsQuery';
import { useVendorsQuery } from '@/features/vendor/hooks/useVendorsQuery';
import { useUserGradePatchMutation } from '../hooks/useUserGradeMutation';
import type { GradeDiscountExclusions, GradeItem } from '../hooks/useUserGradesQuery';

/** 공급사 목록 한 번에 받는 크기 — 입점사는 수십 곳 이하. */
const VENDOR_FETCH_SIZE = 200;

/** "카테고리 4 · 상품 2" — 표 칸 요약. 아무것도 없으면 "없음". */
export const exclusionSummary = (e: GradeDiscountExclusions): string => {
  const parts = [
    ['공급사', e.vendorIds.length],
    ['카테고리', e.categoryIds.length],
    ['브랜드', e.brandIds.length],
    ['상품', e.productIds.length],
  ]
    .filter(([, n]) => (n as number) > 0)
    .map(([label, n]) => `${label} ${n}`);
  return parts.length > 0 ? parts.join(' · ') : '없음';
};

const ChipList = ({
  ids,
  nameOf,
  onRemove,
  testId,
}: {
  ids: ReadonlyArray<string>;
  nameOf: (id: string) => string;
  onRemove: (id: string) => void;
  testId: string;
}) =>
  ids.length === 0 ? (
    <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
      없음
    </p>
  ) : (
    <ul className="flex flex-wrap gap-1.5" data-testid={testId}>
      {ids.map((id) => (
        <li
          key={id}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[12px]"
          style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }}
        >
          {nameOf(id)}
          <button type="button" aria-label={`${nameOf(id)} 빼기`} onClick={() => onRemove(id)} className="px-0.5">
            ×
          </button>
        </li>
      ))}
    </ul>
  );

const Section = ({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h3 className="text-sm font-semibold text-[color:var(--content-strong)]">{title}</h3>
    {hint ? (
      <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
        {hint}
      </p>
    ) : null}
    {children}
  </section>
);

const addId = (list: string[], id: string | null): string[] => (id && !list.includes(id) ? [...list, id] : list);

/**
 * QA-464 회원등급 [할인 제외] — 고도몰 회원등급 [추가 할인] 예외 조건(공급사·카테고리·브랜드·상품). 걸린 상품은 이 등급의
 * 할인을 받지 않는다. 저장하면 네 목록을 통째로 보낸다(user-service PATCH discountExclusions).
 */
export const GradeDiscountExclusionsDialog = ({ grade, onClose }: { grade: GradeItem; onClose: () => void }) => {
  const { t } = useTranslation('common');
  const patch = useUserGradePatchMutation();
  const [value, setValue] = useState<GradeDiscountExclusions>(
    grade.discountExclusions ?? { vendorIds: [], categoryIds: [], brandIds: [], productIds: [] },
  );
  const categories = useAdminCategoryItemsQuery();
  const brands = useBrandsQuery();
  const vendors = useVendorsQuery({ size: VENDOR_FETCH_SIZE });

  const categoryName = (id: string) => {
    const c = (categories.data ?? []).find((x) => x.categoryId === id);
    return c ? `${categoryDisplayName(c)} (${c.categoryCode})` : id;
  };
  const brandName = (id: string) => (brands.data ?? []).find((b) => b.brandId === id)?.brandName ?? id;
  const vendorName = (id: string) => (vendors.data ?? []).find((v) => v.vendorId === id)?.vendorName ?? id;
  const remove = (key: keyof GradeDiscountExclusions, id: string) =>
    setValue((s) => ({ ...s, [key]: s[key].filter((x) => x !== id) }));

  const save = () => {
    patch.mutate(
      { gradeId: grade.gradeId, discountExclusions: value },
      {
        onSuccess: () => {
          toast.success(t('userGrades.exclusions.saved', '할인 제외를 저장했습니다.'));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('userGrades.exclusions.title', { gradeName: grade.gradeName, defaultValue: '{{gradeName}} — 할인 제외' })}
          </DialogTitle>
          <DialogDescription>
            {t(
              'userGrades.exclusions.description',
              '아래에 걸린 상품은 이 등급의 할인을 받지 않습니다(고도몰 회원등급 [추가 할인] 예외 조건).',
            )}
          </DialogDescription>
        </DialogHeader>

        <Section title={t('userGrades.exclusions.categories', '카테고리')} hint={t('userGrades.exclusions.categoriesHint', '하위 카테고리 상품도 제외됩니다.')}>
          <ChipList ids={value.categoryIds} nameOf={categoryName} onRemove={(id) => remove('categoryIds', id)} testId="grade-exclusion-categories" />
          <CategorySearchSelect
            value={null}
            includeHidden
            placeholder={t('userGrades.exclusions.addCategory', '카테고리 추가')}
            onChange={(id) => setValue((s) => ({ ...s, categoryIds: addId(s.categoryIds, id) }))}
            testId="grade-exclusion-category-select"
          />
        </Section>

        <Section title={t('userGrades.exclusions.products', '상품')}>
          <ProductMultiSelectModalField
            value={value.productIds}
            onChange={(productIds) => setValue((s) => ({ ...s, productIds }))}
            emptyHint={t('userGrades.exclusions.productsEmpty', '제외할 상품 없음')}
            testId="grade-exclusion-products"
          />
        </Section>

        <Section title={t('userGrades.exclusions.brands', '브랜드')}>
          <ChipList ids={value.brandIds} nameOf={brandName} onRemove={(id) => remove('brandIds', id)} testId="grade-exclusion-brands" />
          <BrandSelector
            value={null}
            onChange={(id) => setValue((s) => ({ ...s, brandIds: addId(s.brandIds, id) }))}
            testId="grade-exclusion-brand-select"
          />
        </Section>

        <Section title={t('userGrades.exclusions.vendors', '공급사')}>
          <ChipList ids={value.vendorIds} nameOf={vendorName} onRemove={(id) => remove('vendorIds', id)} testId="grade-exclusion-vendors" />
          <Select
            value=""
            onChange={(e) => setValue((s) => ({ ...s, vendorIds: addId(s.vendorIds, e.target.value || null) }))}
            aria-label={t('userGrades.exclusions.addVendor', '공급사 추가')}
            data-testid="grade-exclusion-vendor-select"
          >
            <option value="">{t('userGrades.exclusions.addVendor', '공급사 추가')}</option>
            {(vendors.data ?? [])
              .filter((v) => !value.vendorIds.includes(v.vendorId))
              .map((v) => (
                <option key={v.vendorId} value={v.vendorId}>
                  {v.vendorName}
                </option>
              ))}
          </Select>
        </Section>

        {patch.error ? (
          <p className="text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert">
            {friendlyMessage(patch.error, t('userGrades.exclusions.fail', '할인 제외를 저장하지 못했습니다.'))}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {t('userGrades.cancel', '취소')}
          </Button>
          <Button type="button" size="sm" disabled={patch.isPending} onClick={save} data-testid="grade-exclusion-save">
            {t('userGrades.actions.save', '저장')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
